import { useState } from 'react'
import Styles from './LastMessageActionsV2.module.scss'
import { ChatClient, ChatDesignPreviewContent, ChatGeneratedMediaContent } from 'api/clients/ChatClient'
import { observer } from 'mobx-react-lite'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Button } from 'blaze-ui/Button'
import { InsertRight } from 'blaze-ui/icons/20/InsertRight'
import { useApplyDesign } from 'components/ChatAssistant/useApplyDesign'
import { useReplaceMedia } from 'hooks/queries/designs/useReplaceMedia'
import { useRootStore } from 'stores/RootStore'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'

export type ReplaceableContent =
  | { type: 'design'; content: ChatDesignPreviewContent }
  | { type: 'media'; content: ChatGeneratedMediaContent }

interface ReplaceButtonProps {
  replaceable: ReplaceableContent
  threadId: number
  messageId: number
}

export const ReplaceButton = observer(({ replaceable, threadId, messageId }: ReplaceButtonProps) => {
  const [isReplacing, setIsReplacing] = useState(false)
  const { generationTimestampStore } = useRootStore()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const designId = replaceable.content.design_id
  const isGenerating = !!generationTimestampStore.getStartedAt(designId)
  const { applyDesign } = useApplyDesign({ fileId: designId })
  const { mutateAsync: replaceMedia } = useReplaceMedia({ designId })

  const setFileGenerating = (isGenerating: boolean) => {
    optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, designId, (f) => ({
      ...f,
      is_generating: isGenerating,
      image_url_generating: isGenerating,
    }))
    updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, designId, (file) => ({
      ...file,
      is_generating: isGenerating,
      image_url_generating: isGenerating,
    }))
  }

  const handleReplace = async () => {
    setIsReplacing(true)
    setFileGenerating(true)
    generationTimestampStore.startGeneration(designId, 'default')

    try {
      if (replaceable.type === 'design') {
        const snapshot = replaceable.content.content[0]
        if (snapshot) {
          await applyDesign(snapshot)
        }
      } else {
        const replaceMediaParams: {
          media_file_key?: string
          generation_data?: Record<string, unknown>
          slide_index?: number
        } = {
          media_file_key: replaceable.content.media_file_id,
        }
        if (replaceable.content.generation_data) {
          replaceMediaParams.generation_data = replaceable.content.generation_data
        }
        if (replaceable.content.type === 'generated_image' && replaceable.content.slide_index !== undefined) {
          replaceMediaParams.slide_index = replaceable.content.slide_index
        }
        await replaceMedia(replaceMediaParams)
      }

      if (workspaceId && threadId) {
        ChatClient.trackAction(workspaceId, threadId, {
          action_type: 'accepted',
          chat_message_id: messageId,
          metadata: {},
        }).catch(() => {})
      }
    } catch {
      showToastMessage('Unable to replace', 'error')
      setFileGenerating(false)
      generationTimestampStore.completeGeneration(designId)
    } finally {
      setIsReplacing(false)
    }
  }

  const getLabel = () => {
    if (replaceable.type === 'design') return 'Replace design'
    if (replaceable.content.type === 'generated_video') return 'Replace video in design'
    if (
      replaceable.content.type === 'generated_image' &&
      'slide_index' in replaceable.content &&
      replaceable.content.slide_index !== undefined
    ) {
      return 'Replace slide in design'
    }
    return 'Replace image in design'
  }

  return (
    <Button
      className={Styles.replaceButton}
      frontIcon={InsertRight}
      variant="secondary"
      size="md"
      fullWidth={false}
      isDisabled={isReplacing || isGenerating}
      onPress={handleReplace}
    >
      {isReplacing ? 'Replacing...' : getLabel()}
    </Button>
  )
})
